import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface TimelineEvent {
  date: string;
  description: string;
  type: 'status_change' | 'application_update' | 'note';
}

export function useJobTimeline(jobId: string) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTimeline() {
      try {
        // Fetch job status changes
        const { data: jobHistory, error: jobError } = await supabase
          .from('nrm_jobs')
          .select('status, updated_at')
          .eq('id', jobId)
          .order('updated_at', { ascending: false });

        if (jobError) throw jobError;

        // Fetch application updates
        const { data: applicationUpdates, error: appError } = await supabase
          .from('nrm_job_applications')
          .select(`
            status,
            updated_at,
            contact:contact_id (
              first_name,
              last_name
            )
          `)
          .eq('job_id', jobId)
          .order('updated_at', { ascending: false });

        if (appError) throw appError;

        // Combine and sort events
        const allEvents: TimelineEvent[] = [
          ...(jobHistory || []).map(history => ({
            date: history.updated_at,
            description: `Job status updated to ${history.status}`,
            type: 'status_change' as const
          })),
          ...(applicationUpdates || []).map(update => ({
            date: update.updated_at,
            description: `${update.contact.first_name} ${update.contact.last_name}'s application status updated to ${update.status}`,
            type: 'application_update' as const
          }))
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        setEvents(allEvents);
      } catch (err) {
        console.error('Failed to fetch timeline:', err);
        setError('Failed to load timeline');
      } finally {
        setLoading(false);
      }
    }

    fetchTimeline();
  }, [jobId]);

  return { events, loading, error };
}